/**
 * `<clock-in mount>/missed-shift` — the caller's OWN shift that the clock never
 * saw (POST /timesheets/manual, api#1148). Any role; nothing here names a
 * worker, the API takes the JWT user.
 *
 * It is a CLAIM, and the page says so: it is created with no punch, carries a
 * required reason, and goes straight to Pending for someone ELSE to approve —
 * the owner's own approve is refused (`self_approval`).
 *
 * ── WHAT THE HAND ENTERS ───────────────────────────────────────────────────
 * A day, a start time and an end time, on the TENANT's wall clock. An end at or
 * before the start is read as the next morning — a night shift — and the page
 * says "ends the next day" beside it rather than guessing silently. The day is
 * the tenant's (companies.time_zone), never the device's, because the API
 * checks the clock-in against `date` in that zone (`clock_in_not_on_date`).
 *
 * WHERE has no default, for ClockInScreen's reason: it feeds the safety
 * denominator, and a location nobody chose is a fact nobody stated.
 *
 * ── REFUSALS ────────────────────────────────────────────────────────────────
 * Every 400 and 409 carries a sentence written for the worker, and it is shown
 * as written, on the page, under the button. An overlap also names the shift it
 * collides with (`details.timesheetId`), and the page links to it: the API's
 * own advice is to correct that shift instead of adding another.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { WORK_LOCATIONS, WORK_LOCATION_LABELS, type WorkLocation } from '../../constants/work-locations';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { parseCalendarDate, toCalendarDateString, todayCalendarDate } from '../../lib/calendar-date';
import { fromDatetimeLocal } from '../../lib/datetime-local';
import type { ManualTimesheetBody } from '../../types/timesheet-self-edit';
import { C } from './clockInTheme';
import {
  ClockInPageShell, FIELD_INPUT, FIELD_LABEL, FormError, PrimaryButton, SecondaryButton,
  fieldInputStyle, useClockInReturn,
} from './ClockInPageShell';

/** `YYYY-MM-DD` plus one calendar day, built and read on local fields — never through UTC. */
export function nextCalendarDay(day: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const d = parseCalendarDate(day);
  if (!d) return null;
  d.setDate(d.getDate() + 1);
  return toCalendarDateString(d);
}

/**
 * The request body for a day, two wall-clock times and a place, or the one
 * thing still missing. An end at or before the start ends the next day.
 */
export function buildManualShift(
  input: { date: string; start: string; end: string; where: WorkLocation | null; reason: string },
  timeZone: string | null | undefined,
): { body: ManualTimesheetBody; overnight: boolean } | { missing: string } {
  if (!input.date) return { missing: 'Pick the day you worked.' };
  if (!input.start) return { missing: 'Pick the time you started.' };
  if (!input.end) return { missing: 'Pick the time you finished.' };
  if (!input.where) return { missing: 'Pick shop or field.' };
  if (!input.reason.trim()) return { missing: 'Say why the clock missed this shift.' };
  const overnight = input.end <= input.start;
  const endDay = overnight ? nextCalendarDay(input.date) : input.date;
  const clockIn = fromDatetimeLocal(`${input.date}T${input.start}`, timeZone);
  const clockOut = endDay ? fromDatetimeLocal(`${endDay}T${input.end}`, timeZone) : null;
  if (!clockIn || !clockOut) return { missing: 'Those times could not be read. Pick them again.' };
  return {
    overnight,
    body: {
      date: input.date,
      clock_in_at: clockIn,
      clock_out_at: clockOut,
      work_location: input.where,
      reason: input.reason.trim(),
    },
  };
}

export default function MissedShiftScreen() {
  const timeZone = useTenantTimeZone();
  const qc = useQueryClient();
  const { base, back } = useClockInReturn();
  const today = todayCalendarDate(timeZone) ?? '';

  const [date, setDate] = useState(today);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [where, setWhere] = useState<WorkLocation | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<{ message: string; overlapId?: string } | null>(null);

  const built = useMemo(
    () => buildManualShift({ date, start, end, where, reason }, timeZone),
    [date, start, end, where, reason, timeZone],
  );
  const overnight = !!start && !!end && end <= start;

  const save = useMutation({
    mutationFn: async () => {
      if ('missing' in built) throw Object.assign(new Error(built.missing), { local: true });
      const res = await api.timesheets.createManual(built.body);
      return res.data?.data;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['timesheets', 'my-period'] });
      qc.invalidateQueries({ queryKey: ['timesheet-status'] });
      // The hours landed either way; whether they reached the queue is what
      // the API reports, and an absent `submitted` is not read as success.
      back(row?.submitted === true
        ? 'Missed shift sent for approval'
        : `Shift saved, but not sent for approval${row?.submission_error ? `: ${row.submission_error}` : '.'}`);
    },
    onError: (err: any) => {
      if (err?.local) { setError({ message: err.message }); return; }
      const data = err?.response?.data;
      const overlapId = data?.code === 'overlapping_shift' && typeof data?.details?.timesheetId === 'string'
        ? data.details.timesheetId as string
        : undefined;
      setError({
        message: typeof data?.error === 'string' && data.error
          ? data.error
          : err?.message ? `Could not save: ${err.message}` : 'Could not save. Check your connection and try again.',
        overlapId,
      });
    },
  });

  const clear = () => setError(null);

  return (
    <ClockInPageShell
      title="Add a missed shift"
      subtitle="For a shift you worked but never clocked. It goes to the office for approval."
      onBack={() => back()}
    >
      <form
        aria-label="Add a missed shift"
        className="rounded-2xl p-5"
        style={{ background: C.surface }}
        onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
      >
        <label htmlFor="missed-date" className={FIELD_LABEL} style={{ color: C.onSurfaceVariant }}>Day worked</label>
        <input
          id="missed-date"
          type="date"
          value={date}
          max={today || undefined}
          onChange={(e) => { setDate(e.target.value); clear(); }}
          className={FIELD_INPUT}
          style={fieldInputStyle}
        />

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <label htmlFor="missed-start" className={FIELD_LABEL} style={{ color: C.onSurfaceVariant }}>Started</label>
            <input
              id="missed-start"
              type="time"
              value={start}
              onChange={(e) => { setStart(e.target.value); clear(); }}
              className={FIELD_INPUT}
              style={fieldInputStyle}
            />
          </div>
          <div>
            <label htmlFor="missed-end" className={FIELD_LABEL} style={{ color: C.onSurfaceVariant }}>Finished</label>
            <input
              id="missed-end"
              type="time"
              value={end}
              onChange={(e) => { setEnd(e.target.value); clear(); }}
              aria-describedby={overnight ? 'missed-overnight' : undefined}
              className={FIELD_INPUT}
              style={fieldInputStyle}
            />
          </div>
        </div>
        {overnight && (
          <p id="missed-overnight" className="text-xs mt-1.5" style={{ color: C.onSurfaceVariant }}>
            Ends the next day.
          </p>
        )}

        <fieldset className="mt-4">
          <legend className={FIELD_LABEL} style={{ color: C.onSurfaceVariant }}>Where</legend>
          <div className="grid grid-cols-2 gap-3">
            {WORK_LOCATIONS.map((loc) => (
              <button
                key={loc}
                type="button"
                aria-pressed={where === loc}
                onClick={() => { setWhere(loc); clear(); }}
                className="rounded-xl py-3 text-sm font-semibold"
                style={where === loc
                  ? { background: 'var(--ci-confirm)', color: 'var(--ci-on-confirm)' }
                  : { border: '1px solid var(--ci-edge-25)', color: C.onSurface, background: 'transparent' }}
              >
                {WORK_LOCATION_LABELS[loc]}
              </button>
            ))}
          </div>
        </fieldset>

        <label htmlFor="missed-reason" className={`${FIELD_LABEL} mt-4`} style={{ color: C.onSurfaceVariant }}>Why wasn't it clocked?</label>
        <textarea
          id="missed-reason"
          value={reason}
          onChange={(e) => { setReason(e.target.value); clear(); }}
          placeholder="e.g. Phone was dead at the rig"
          aria-describedby="missed-reason-hint"
          className={`${FIELD_INPUT} resize-none`}
          style={{ ...fieldInputStyle, minHeight: 80 }}
        />
        <p id="missed-reason-hint" className="text-xs mt-1.5" style={{ color: C.onSurfaceVariant }}>
          Required. The approver sees it with the shift.
        </p>

        {error && (
          <FormError>
            {error.message}
            {error.overlapId && (
              <>
                {' '}
                <Link to={`${base}/shift/${error.overlapId}/edit`} className="underline font-semibold">
                  Open that shift
                </Link>
              </>
            )}
          </FormError>
        )}

        <div className="space-y-2 mt-5">
          <PrimaryButton busy={save.isPending} disabled={'missing' in built} onClick={() => save.mutate()}>
            Send for approval
          </PrimaryButton>
          {'missing' in built && (
            <p className="text-xs text-center" style={{ color: C.onSurfaceVariant }}>{built.missing}</p>
          )}
          <SecondaryButton disabled={save.isPending} onClick={() => back()}>Cancel</SecondaryButton>
        </div>
      </form>
    </ClockInPageShell>
  );
}
